import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GuardianAthleteDto } from '../types/guardian';
import { isGuardian } from '../utils/auth';
import '../styles/guardian-athlete-selector.css';

export default function GuardianAthleteSelector() {
    const navigate = useNavigate();
    const location = useLocation();
    const [athletes, setAthletes] = useState<GuardianAthleteDto[]>([]);
    const [selectedAthleteId, setSelectedAthleteId] = useState<number | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Only show this component if user is a guardian
        if (!isGuardian()) {
            setIsVisible(false);
            return;
        }

        // Get guardian athletes from localStorage
        const athletesData = localStorage.getItem('guardianAthletes');
        if (!athletesData) {
            setIsVisible(false);
            return;
        }

        try {
            const parsedAthletes: GuardianAthleteDto[] = JSON.parse(athletesData);

            // Only show if there are multiple athletes
            if (parsedAthletes && parsedAthletes.length > 1) {
                setAthletes(parsedAthletes);

                // Get currently selected athlete
                const currentAthleteId = localStorage.getItem('guardianViewingAthleteId');
                if (currentAthleteId) {
                    setSelectedAthleteId(Number(currentAthleteId));
                }

                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        } catch (error) {
            setIsVisible(false);
        }
    }, []);

    const handleAthleteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newAthleteId = Number(event.target.value);

        if (newAthleteId && newAthleteId !== selectedAthleteId) {
            // Update the selected athlete
            localStorage.setItem('guardianViewingAthleteId', String(newAthleteId));
            setSelectedAthleteId(newAthleteId);

            // Refresh the current page to load new athlete's data
            // Use navigate with replace to avoid adding to history
            navigate(location.pathname, { replace: true });

            // Trigger a page refresh to reload all data
            window.location.reload();
        }
    };

    // Don't render anything if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <div className="guardian-athlete-selector">
            <label htmlFor="guardian-athlete-dropdown" className="guardian-athlete-selector-label">
                Select athlete:
            </label>
            <select
                id="guardian-athlete-dropdown"
                className="guardian-athlete-dropdown"
                value={selectedAthleteId || ''}
                onChange={handleAthleteChange}
                aria-label="Select athlete to view"
            >
                {athletes.map((athlete) => (
                    <option key={athlete.athleteId} value={athlete.athleteId}>
                        {athlete.displayName || `${athlete.firstName} ${athlete.lastName}`}
                    </option>
                ))}
            </select>
        </div>
    );
}

